import { useUserGrantsContext } from "features/user/hooks/useUserGrantsContext";
import { ImportModalProps } from "./ImportModal";
import { checkOperationPermission } from "features/auth/utils/checkOperationPermission";
import { useForm } from "react-hook-form";
import { useState } from "react";
import { UploadControllerService } from "openapi";
import authorizedCall from "features/common/utils/authorizedCall";
import { toIsoString } from "features/common/lib/date";
import { AlertColor } from "@mui/material";

export interface useImportModalArgs {
  onClose: ImportModalProps["onClose"];
  type: ImportModalProps["type"];
}

export interface ImportFormInputs {
  filename: string;
}

const defaultValues = {
  filename: "",
};

export const useImportModal = ({ onClose, type }: useImportModalArgs) => {
  const [file, setFile] = useState<File>();
  const [importError, setImportError] = useState("");
  const [importResult, setImportResult] = useState("");
  const { userGrants } = useUserGrantsContext();

  const {
    handleSubmit,
    control,
    setValue,
    trigger,
    formState: { isSubmitting },
  } = useForm<ImportFormInputs>({
    defaultValues,
  });

  const renderImportMessage = !isSubmitting && (importError || importResult);
  const showImportMessage = !!(importError || importResult);
  const importMessageSeverity: AlertColor = importError ? "error" : "success";
  const alertMessage = importError ? importError : importResult;

  const componentTypeMap = {
    PORTFOLIO: "Import Portfolio",
    MASTER: "Import Masterfile",
  };

  const importIsAllowed =
    userGrants &&
    checkOperationPermission({
      operation: "E",
      component: componentTypeMap[type],
      func: type === "MASTER" ? "IMPORT_MASTER" : "IMPORT_PORTFOLIO",
      userGrants,
    });

  const clearMessages = () => {
    setImportError("");
    setImportResult("");
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFile(e.target.files[0]);
      setValue("filename", e.target.files[0]?.name);
      trigger("filename");
      clearMessages();
    }
  };

  const handleClose = () => {
    onClose();
  };

  const onAlertRemove = () => {
    setValue("filename", "");
  };

  const onSubmit = async () => {
    if (!file) {
      return;
    }

    clearMessages();

    try {
      const {
        _embedded: { entities },
      } = await authorizedCall(
        UploadControllerService.uploadFile,
        {
          timestamp: toIsoString(new Date()),
          type,
          formData: {
            file,
          },
        },
        true
      );
      setImportResult(entities?.[0].reportId);
    } catch (error: any) {
      setImportError(error.body?.message || error.message);
    }
  };

  return {
    handleClose,
    importIsAllowed,
    onSubmit: handleSubmit(onSubmit),
    handleFileChange,
    control,
    isSubmitting,
    importError,
    importResult,
    renderImportMessage,
    showImportMessage,
    importMessageSeverity,
    alertMessage,
    onAlertRemove,
  };
};
